import { useState, useRef } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorMessage from '@/components/common/ErrorMessage';
import { env } from '@/lib/env';
import {
  useProductCatalogStats,
  usePreviewCatalogUpload,
  useConfirmCatalogImport,
} from '@/hooks/useProductCatalogAdmin';
import type { PreviewResult, ImportResult } from '@/hooks/useProductCatalogAdmin';

type PageState = 'initial' | 'previewing' | 'importing' | 'result';

export default function AdminProductCatalog() {
  const [pageState, setPageState] = useState<PageState>('initial');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: stats, isLoading: statsLoading, error: statsError } = useProductCatalogStats();
  const previewMutation = usePreviewCatalogUpload();
  const confirmMutation = useConfirmCatalogImport();

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('auth_token');
    const url = `${env.API_BASE_URL}/admin/product-catalog/template`;
    const link = document.createElement('a');
    link.href = url;
    if (token) {
      // Use fetch with auth header for protected endpoint
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          link.href = blobUrl;
          link.download = 'product-catalog-template.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        });
    } else {
      link.download = 'product-catalog-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset all errors and state
    setUploadError(null);
    setImportError(null);
    setFileTypeError(null);
    setImportResult(null);
    setPreview(null);

    // Validate file type
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setFileTypeError('Only CSV files are accepted.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    previewMutation.mutate(file, {
      onSuccess: (data) => {
        setPreview(data);
        setPageState('previewing');
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } } };
        setUploadError(
          err?.response?.data?.error || err?.message || 'Upload failed. Please try again.'
        );
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  };

  const handleConfirmImport = () => {
    if (!preview) return;
    setPageState('importing');
    setImportError(null);

    confirmMutation.mutate(preview.previewId, {
      onSuccess: (data) => {
        setImportResult(data);
        setPageState('result');
        setPreview(null);
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } } };
        setImportError(
          err?.response?.data?.error || err?.message || 'Import failed. Please try again.'
        );
        setPageState('previewing');
      },
    });
  };

  const handleCancel = () => {
    setPreview(null);
    setPageState('initial');
    setUploadError(null);
    setImportError(null);
    setFileTypeError(null);
  };

  const handleReset = () => {
    setImportResult(null);
    setPreview(null);
    setPageState('initial');
    setUploadError(null);
    setImportError(null);
    setFileTypeError(null);
  };

  const formatLastUpdated = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-[22px] font-bold text-neutral-900 uppercase tracking-tight">
          Product Catalog
        </h1>
        <Button variant="outline" onClick={handleDownloadTemplate}>
          Download Template
        </Button>
      </div>

      {/* Stats Section */}
      {statsLoading ? (
        <div className="flex justify-center items-center h-32">
          <LoadingSpinner />
        </div>
      ) : statsError ? (
        <ErrorMessage message="Failed to load catalog statistics" />
      ) : stats ? (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Total Products
              </p>
              <p className="text-[28px] font-bold text-neutral-900 mt-1">
                {stats.total.toLocaleString()}
              </p>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Last Updated
              </p>
              <p className="text-sm font-medium text-neutral-900 mt-1">
                {formatLastUpdated(stats.lastUpdated)}
              </p>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Digital
              </p>
              <p className="text-[28px] font-bold text-neutral-900 mt-1">
                {stats.digitalCount.toLocaleString()}
              </p>
              <p className="text-[13px] font-medium text-neutral-400 mt-0.5">
                {stats.nonDigitalCount.toLocaleString()} non-digital
              </p>
            </Card>
            <Card>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Regions
              </p>
              <p className="text-[28px] font-bold text-neutral-900 mt-1">
                {Object.keys(stats.byRegion).length}
              </p>
            </Card>
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <h2 className="text-[16px] font-semibold uppercase tracking-wide text-neutral-900 mb-3">
                By Region
              </h2>
              {Object.keys(stats.byRegion).length === 0 ? (
                <p className="text-sm text-neutral-400">No data</p>
              ) : (
                <ul className="space-y-1.5">
                  {Object.entries(stats.byRegion).map(([region, count]) => (
                    <li key={region} className="flex justify-between text-sm">
                      <span className="text-neutral-600">{region}</span>
                      <span className="font-medium text-neutral-900">{count.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card>
              <h2 className="text-[16px] font-semibold uppercase tracking-wide text-neutral-900 mb-3">
                By Market
              </h2>
              {Object.keys(stats.byMarket).length === 0 ? (
                <p className="text-sm text-neutral-400">No data</p>
              ) : (
                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                  {Object.entries(stats.byMarket).map(([market, count]) => (
                    <li key={market} className="flex justify-between text-sm">
                      <span className="text-neutral-600">{market}</span>
                      <span className="font-medium text-neutral-900">{count.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card>
              <h2 className="text-[16px] font-semibold uppercase tracking-wide text-neutral-900 mb-3">
                By Type
              </h2>
              {Object.keys(stats.byType).length === 0 ? (
                <p className="text-sm text-neutral-400">No data</p>
              ) : (
                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                  {Object.entries(stats.byType).map(([type, count]) => (
                    <li key={type} className="flex justify-between text-sm">
                      <span className="text-neutral-600">{type}</span>
                      <span className="font-medium text-neutral-900">{count.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <p className="text-sm text-neutral-400">No catalog data is loaded.</p>
        </Card>
      )}

      {/* Upload Section — visible in initial and result states */}
      {(pageState === 'initial' || pageState === 'result') && (
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={previewMutation.isPending}
              className="block w-full text-sm text-neutral-400
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-[#EEE9F9] file:text-[#6B4FBB]
                hover:file:bg-[#EEE9F9]
                disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {previewMutation.isPending && <LoadingSpinner size="sm" />}
          </div>
          {fileTypeError && (
            <p className="mt-2 text-sm text-[#B91C1C]">{fileTypeError}</p>
          )}
        </Card>
      )}

      {/* Upload Error Banner */}
      {uploadError && (
        <div className="border-l-4 border-[#B91C1C] bg-[#FEE2E2] rounded-r-lg px-4 py-3">
          <p className="text-sm font-medium text-[#B91C1C]">{uploadError}</p>
        </div>
      )}

      {/* Import Error Banner */}
      {importError && (
        <div className="border-l-4 border-[#B91C1C] bg-[#FEE2E2] rounded-r-lg px-4 py-3">
          <p className="text-sm font-medium text-[#B91C1C]">{importError}</p>
        </div>
      )}

      {/* Preview State */}
      {pageState === 'previewing' && preview && (
        <Card>
          <h2 className="text-[16px] font-semibold uppercase tracking-wide text-neutral-900 mb-4">
            Import Preview
          </h2>

          {/* Preview Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Valid Records
              </p>
              <p className="text-[28px] font-bold text-[#1A7A3C]">
                {preview.validCount.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Row Errors
              </p>
              <p className={`text-[28px] font-bold ${preview.errorCount > 0 ? 'text-[#B91C1C]' : 'text-neutral-900'}`}>
                {preview.errorCount.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Total Rows Processed
              </p>
              <p className="text-[28px] font-bold text-neutral-900">
                {preview.totalRowsProcessed.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-neutral-600">
                Current → New Count
              </p>
              <p className="text-sm font-medium text-neutral-900 mt-1">
                {stats?.total.toLocaleString() ?? '0'} → {preview.validCount.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Row Errors */}
          {preview.rowErrors.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#B91C1C] mb-2">
                Row Errors ({preview.rowErrors.length})
              </h3>
              <ul className="list-disc list-inside text-sm text-[#B91C1C] max-h-40 overflow-y-auto bg-[#FEE2E2] rounded-lg p-3">
                {preview.rowErrors.map((err, idx) => (
                  <li key={idx}>
                    Row {err.row} — {err.field}: {err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sample Rows Table */}
          {preview.sampleRows.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-neutral-900 mb-2">
                Sample Rows (first {preview.sampleRows.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      {Object.keys(preview.sampleRows[0] ?? {}).map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-neutral-600"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-100">
                    {preview.sampleRows.map((row, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-[#EEE9F9]">
                        {Object.values(row).map((val, colIdx) => (
                          <td
                            key={colIdx}
                            className="px-4 py-3 whitespace-nowrap text-sm text-neutral-900"
                          >
                            {val ?? '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="primary" onClick={handleConfirmImport}>
              Confirm Import
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Importing State */}
      {pageState === 'importing' && (
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-sm font-medium text-neutral-600">
              Importing catalog data…
            </p>
          </div>
        </Card>
      )}

      {/* Result State */}
      {pageState === 'result' && importResult && (
        <>
          {/* Success Banner */}
          <div className="border-l-4 border-[#1A7A3C] bg-[#E8F5ED] rounded-r-lg px-4 py-3">
            <p className="text-sm font-medium text-[#1A7A3C]">
              Successfully loaded {importResult.recordsLoaded.toLocaleString()} of{' '}
              {importResult.totalRowsProcessed.toLocaleString()} rows.
            </p>
          </div>

          {/* Row Errors from Import */}
          {importResult.rowErrors.length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold text-[#92400E] mb-2">
                Row Errors ({importResult.rowErrors.length})
              </h3>
              <ul className="list-disc list-inside text-sm text-[#92400E] max-h-60 overflow-y-auto bg-[#FEF3C7] rounded-lg p-3">
                {importResult.rowErrors.map((err, idx) => (
                  <li key={idx}>
                    Row {err.row} — {err.field}: {err.message}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Button variant="outline" onClick={handleReset}>
            Upload Another File
          </Button>
        </>
      )}
    </div>
  );
}
